import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "../../layout/Sidebar";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import { can } from "../../utils/can";

const DEFAULT_PRESET_ICONS = [
  { label: "Groom Icon", value: "assets/img/icons/staticon1.png" },
  { label: "Bride Icon", value: "assets/img/icons/staticon2.png" },
  { label: "Divorcee Groom Icon", value: "assets/img/icons/staticon3.png" },
  { label: "Divorcee Bride Icon", value: "assets/img/icons/staticon4.png" },
  { label: "Success Stories Icon", value: "assets/img/icons/staticon5.png" },
];

export default function NumberStatsCMS() {
  const API_URL = (process.env.REACT_APP_BASE_URL_API || "").replace(/\/$/, "");
  const token = localStorage.getItem("adminToken");
  const fileInputRef = useRef(null);

  const [stats, setStats] = useState([]);
  const [liveMap, setLiveMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Edit / Add Card State
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    key: "",
    label: "",
    countType: "Auto",
    manualCount: 0,
    offset: 0,
    order: 0,
    status: "Active",
    icon: "assets/img/icons/staticon1.png",
    iconFile: null,
  });

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg("");
      const res = await fetch(`${API_URL}/api/admin/number-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) {
        setStats(result.data || []);
        setLiveMap(result.liveMap || {});
      } else {
        setErrorMsg(result.message || "Failed to load stats");
      }
    } catch (err) {
      console.error("Fetch number stats error:", err);
      setErrorMsg("Unable to connect to server");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Handle in-grid state change for bulk editing
  const handleStatChange = (index, field, value) => {
    setStats((prev) => {
      const updated = [...prev];
      const item = { ...updated[index], [field]: value };
      
      const live = liveMap[item.key] ?? item.liveCount ?? 0;
      if (item.countType === "Manual") {
        item.currentCount = Number(item.manualCount || 0);
      } else if (item.countType === "AutoWithOffset") {
        item.currentCount = Number(live) + Number(item.offset || 0);
      } else {
        item.currentCount = Number(live);
      }
      
      updated[index] = item;
      return updated;
    });
  };

  // Bulk save all stats
  const handleBulkSave = async () => {
    if (can("cms_number_stats", "edit") === false) {
      alert("You do not have permission to edit number stats.");
      return;
    }

    try {
      setSavingBulk(true);
      setMessage("");
      setErrorMsg("");

      const res = await fetch(`${API_URL}/api/admin/number-stats/bulk-update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stats }),
      });

      const result = await res.json();
      if (result.success) {
        setMessage("All number stats saved successfully!");
        setTimeout(() => setMessage(""), 4000);
        fetchStats();
      } else {
        setErrorMsg(result.message || "Failed to update stats.");
      }
    } catch (err) {
      console.error("Bulk save error:", err);
      setErrorMsg("Server error while saving.");
    } finally {
      setSavingBulk(false);
    }
  };

  // Submit single Add/Edit Form
  const handleSubmitForm = async (e) => {
    e.preventDefault();

    const action = editId ? "edit" : "create";
    if (can("cms_number_stats", action) === false) {
      alert("You do not have permission.");
      return;
    }

    if (!form.label.trim()) {
      alert("Please enter a label for the counter stat.");
      return;
    }

    try {
      setLoading(true);
      setMessage("");
      setErrorMsg("");

      const formData = new FormData();
      formData.append("label", form.label.trim());
      formData.append("key", form.key.trim() || form.label.toLowerCase().replace(/[^a-z0-9]/g, "_"));
      formData.append("countType", form.countType);
      formData.append("manualCount", form.manualCount || 0);
      formData.append("offset", form.offset || 0);
      formData.append("order", form.order || 0);
      formData.append("status", form.status);

      if (form.iconFile) {
        formData.append("icon", form.iconFile);
      } else if (form.icon) {
        formData.append("icon", form.icon);
      }

      const url = editId
        ? `${API_URL}/api/admin/number-stats/update/${editId}`
        : `${API_URL}/api/admin/number-stats/create`;

      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const result = await res.json();
      if (result.success) {
        setMessage(editId ? "Stat card updated successfully!" : "New stat card added successfully!");
        setTimeout(() => setMessage(""), 4000);
        resetForm();
        fetchStats();
      } else {
        setErrorMsg(result.message || "Failed to save stat card.");
      }
    } catch (err) {
      console.error("Submit form error:", err);
      setErrorMsg("Server error.");
    } finally {
      setLoading(false);
    }
  };

  // Edit handler
  const handleEdit = (item) => {
    setEditId(item._id);
    setForm({
      key: item.key || "",
      label: item.label || "",
      countType: item.countType || "Auto",
      manualCount: item.manualCount || 0,
      offset: item.offset || 0,
      order: item.order || 0,
      status: item.status || "Active",
      icon: item.icon || "assets/img/icons/staticon1.png",
      iconFile: null,
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  // Delete handler
  const handleDelete = async (id, label) => {
    if (can("cms_number_stats", "delete") === false) {
      alert("You do not have permission to delete.");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${label}"?`)) return;

    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/number-stats/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) {
        setMessage("Stat card deleted successfully!");
        setTimeout(() => setMessage(""), 4000);
        if (editId === id) resetForm();
        fetchStats();
      } else {
        alert(result.message || "Failed to delete stat.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Server error.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditId(null);
    setForm({
      key: "",
      label: "",
      countType: "Auto",
      manualCount: 0,
      offset: 0,
      order: 0,
      status: "Active",
      icon: "assets/img/icons/staticon1.png",
      iconFile: null,
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resolveIconUrl = (iconPath) => {
    if (!iconPath) return `${process.env.PUBLIC_URL || ""}/assets/images/logo.png`;
    if (iconPath.startsWith("http://") || iconPath.startsWith("https://")) return iconPath;
    if (iconPath.startsWith("assets/")) return `https://vivashri.com/${iconPath}`;
    if (iconPath.startsWith("/")) return `${API_URL}${iconPath}`;
    return `${API_URL}/${iconPath}`;
  };

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="CMS - Number Stats (Counters)" />

        <div className="right_col" role="main">
          {/* Notifications */}
          {message && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <strong>Success!</strong> {message}
            </div>
          )}
          {errorMsg && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <strong>Error!</strong> {errorMsg}
            </div>
          )}

          {/* 1. Live Visual Preview */}
          <div className="x_panel" style={{ borderRadius: "12px", border: "1px solid #e3e6f0" }}>
            <div className="x_title d-flex justify-content-between align-items-center">
              <div>
                <h2 style={{ fontSize: "1.2rem", fontWeight: "700", color: "#bf3272", margin: 0 }}>
                  <i className="far fa-eye me-2"></i> Live Website Preview
                </h2>
                <small className="text-muted">This is how the statistics strip will display on the Home Page</small>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchStats}
                disabled={loading}
              >
                <i className="far fa-sync-alt me-1"></i> Refresh Live Data
              </button>
            </div>
            <div className="x_content">
              <div
                style={{
                  background: "linear-gradient(135deg, #bf3272 0%, #d84380 50%, #ea5c95 100%)",
                  borderRadius: "14px",
                  padding: "30px 20px",
                  boxShadow: "0 8px 24px rgba(191, 50, 114, 0.25)",
                }}
              >
                <div className="row justify-content-center text-center">
                  {stats
                    .filter((s) => s.status === "Active")
                    .map((stat, idx) => (
                      <div className="col-6 col-md-4 col-lg my-2" key={stat._id || idx}>
                        <div
                          style={{
                            background: "rgba(255, 255, 255, 0.12)",
                            backdropFilter: "blur(6px)",
                            border: "1px solid rgba(255, 255, 255, 0.25)",
                            borderRadius: "12px",
                            padding: "16px 10px",
                            minHeight: "130px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={resolveIconUrl(stat.icon)}
                            alt={stat.label}
                            style={{ width: "38px", height: "38px", objectFit: "contain", marginBottom: "8px" }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                          <div
                            style={{
                              fontSize: "1.8rem",
                              fontWeight: "800",
                              color: "#ffffff",
                              lineHeight: "1.2",
                            }}
                          >
                            {Number(stat.currentCount || 0).toLocaleString()}
                          </div>
                          <div
                            style={{
                              fontSize: "0.95rem",
                              color: "#ffeaf4",
                              fontWeight: "600",
                              marginTop: "4px",
                            }}
                          >
                            {stat.label}
                          </div>
                          <span
                            className="badge mt-2"
                            style={{
                              backgroundColor:
                                stat.countType === "Manual"
                                  ? "#ffc107"
                                  : stat.countType === "AutoWithOffset"
                                  ? "#17a2b8"
                                  : "#28a745",
                              color: stat.countType === "Manual" ? "#000" : "#fff",
                              fontSize: "0.7rem",
                            }}
                          >
                            {stat.countType === "Manual"
                              ? "Fixed Custom"
                              : stat.countType === "AutoWithOffset"
                              ? `Live + ${stat.offset}`
                              : "Live Count"}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* 2. Quick Bulk Counter Settings */}
          <div className="x_panel" style={{ borderRadius: "12px", border: "1px solid #e3e6f0" }}>
            <div className="x_title d-flex justify-content-between align-items-center">
              <div>
                <h2 style={{ fontSize: "1.15rem", fontWeight: "700", color: "#333", margin: 0 }}>
                  <i className="far fa-sliders-h me-2" style={{ color: "#bf3272" }}></i> Quick Counter Settings
                </h2>
                <small className="text-muted">
                  Switch between Live Database Count and Fixed Custom Numbers for each stat
                </small>
              </div>
              <button
                type="button"
                className="btn sitebtn"
                onClick={handleBulkSave}
                disabled={savingBulk || loading}
              >
                <i className="far fa-save me-1"></i> {savingBulk ? "Saving..." : "Save All Changes"}
              </button>
            </div>
            <div className="x_content">
              <div className="row">
                {stats.map((item, idx) => {
                  const live = liveMap[item.key] ?? item.liveCount ?? 0;
                  return (
                    <div className="col-12 col-md-6 col-lg-4 mb-3" key={item._id || idx}>
                      <div
                        className="card h-100"
                        style={{
                          border: item.status === "Active" ? "1.5px solid #ffb6d5" : "1px dashed #ccc",
                          borderRadius: "12px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                          background: item.status === "Active" ? "#ffffff" : "#fdfdfd",
                        }}
                      >
                        <div
                          className="card-header d-flex justify-content-between align-items-center py-2"
                          style={{
                            backgroundColor: item.status === "Active" ? "#fff3f8" : "#f8f9fa",
                            borderBottom: "1px solid #ffe1ee",
                            borderRadius: "12px 12px 0 0",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <img
                              src={resolveIconUrl(item.icon)}
                              alt={item.label}
                              style={{ width: "24px", height: "24px", objectFit: "contain", marginRight: "8px" }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                            <strong style={{ color: "#bf3272", fontSize: "0.95rem" }}>{item.label}</strong>
                          </div>
                          <span className="badge bg-secondary" style={{ fontSize: "0.75rem" }}>
                            Live DB: {Number(live).toLocaleString()}
                          </span>
                        </div>

                        <div className="card-body p-3">
                          {/* Label input */}
                          <div className="mb-2">
                            <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: "600" }}>
                              Display Label
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.label}
                              onChange={(e) => handleStatChange(idx, "label", e.target.value)}
                            />
                          </div>

                          {/* Mode selection */}
                          <div className="mb-2">
                            <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: "600" }}>
                              Count Mode
                            </label>
                            <select
                              className="form-select form-select-sm"
                              value={item.countType || "Auto"}
                              onChange={(e) => handleStatChange(idx, "countType", e.target.value)}
                            >
                              <option value="Auto">Auto (Live Database Count)</option>
                              <option value="Manual">Manual (Fixed Custom Number)</option>
                              <option value="AutoWithOffset">Auto + Custom Offset</option>
                            </select>
                          </div>

                          {/* Value inputs based on mode */}
                          {item.countType === "Manual" && (
                            <div className="mb-2">
                              <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: "600", color: "#856404" }}>
                                Custom Number to Display
                              </label>
                              <input
                                type="number"
                                min="0"
                                className="form-control form-control-sm"
                                style={{ borderColor: "#ffeeba", backgroundColor: "#fffdf5" }}
                                value={item.manualCount ?? 0}
                                onChange={(e) => handleStatChange(idx, "manualCount", Number(e.target.value))}
                              />
                            </div>
                          )}

                          {item.countType === "AutoWithOffset" && (
                            <div className="mb-2">
                              <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: "600", color: "#0c5460" }}>
                                Extra Offset to Add (+ or -)
                              </label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                style={{ borderColor: "#b8daff", backgroundColor: "#f8fbff" }}
                                value={item.offset ?? 0}
                                onChange={(e) => handleStatChange(idx, "offset", Number(e.target.value))}
                              />
                            </div>
                          )}

                          <div className="row g-2 mt-1">
                            <div className="col-6">
                              <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: "600" }}>
                                Status
                              </label>
                              <select
                                className="form-select form-select-sm"
                                value={item.status || "Active"}
                                onChange={(e) => handleStatChange(idx, "status", e.target.value)}
                              >
                                <option value="Active">Active (Show)</option>
                                <option value="Inactive">Inactive (Hide)</option>
                              </select>
                            </div>
                            <div className="col-6">
                              <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: "600" }}>
                                Display Order
                              </label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={item.order ?? 0}
                                onChange={(e) => handleStatChange(idx, "order", Number(e.target.value))}
                              />
                            </div>
                          </div>

                          <div className="mt-3 pt-2 border-top d-flex justify-content-between align-items-center">
                            <span style={{ fontSize: "0.8rem", color: "#666" }}>
                              Current Output:{" "}
                              <strong style={{ color: "#bf3272" }}>
                                {Number(item.currentCount || 0).toLocaleString()}
                              </strong>
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-primary p-0"
                              onClick={() => handleEdit(item)}
                            >
                              <i className="far fa-edit me-1"></i> Edit Details
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-end mt-3">
                <button
                  type="button"
                  className="btn sitebtn px-4 py-2"
                  onClick={handleBulkSave}
                  disabled={savingBulk || loading}
                >
                  <i className="far fa-save me-2"></i>
                  {savingBulk ? "Saving Changes..." : "Save All Changes"}
                </button>
              </div>
            </div>
          </div>

          {/* 3. Detailed Card Form (Add / Edit) */}
          <div className="x_panel" style={{ borderRadius: "12px", border: "1px solid #e3e6f0" }}>
            <div className="x_title d-flex justify-content-between align-items-center">
              <div>
                <h2 style={{ fontSize: "1.15rem", fontWeight: "700", color: "#333", margin: 0 }}>
                  <i className="far fa-edit me-2" style={{ color: "#bf3272" }}></i>
                  {editId ? `Edit Stat Card: ${form.label}` : "Add New Stat Counter Card"}
                </h2>
                <small className="text-muted">Configure custom icons, labels, and advanced settings</small>
              </div>
              {editId && (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetForm}>
                  Cancel Edit / Add New
                </button>
              )}
            </div>

            <div className="x_content">
              <form onSubmit={handleSubmitForm}>
                <div className="row">
                  <div className="col-12 col-md-4 mb-3">
                    <label className="form-label">
                      Stat Label / Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Verified Profiles, Happy Couples"
                      value={form.label}
                      onChange={(e) => setForm({ ...form, label: e.target.value })}
                      required
                    />
                  </div>

                  <div className="col-12 col-md-4 mb-3">
                    <label className="form-label">Key Identifier (Optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. verified_profiles (unique)"
                      value={form.key}
                      onChange={(e) => setForm({ ...form, key: e.target.value })}
                    />
                  </div>

                  <div className="col-12 col-md-4 mb-3">
                    <label className="form-label">Calculation Mode</label>
                    <select
                      className="form-select"
                      value={form.countType}
                      onChange={(e) => setForm({ ...form, countType: e.target.value })}
                    >
                      <option value="Auto">Auto (Live Database Count)</option>
                      <option value="Manual">Manual (Custom Fixed Number)</option>
                      <option value="AutoWithOffset">Auto + Custom Offset</option>
                    </select>
                  </div>

                  {form.countType === "Manual" && (
                    <div className="col-12 col-md-4 mb-3">
                      <label className="form-label">Fixed Custom Count</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        placeholder="e.g. 5000"
                        value={form.manualCount}
                        onChange={(e) => setForm({ ...form, manualCount: Number(e.target.value) })}
                      />
                    </div>
                  )}

                  {form.countType === "AutoWithOffset" && (
                    <div className="col-12 col-md-4 mb-3">
                      <label className="form-label">Custom Offset (Added to Live Count)</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="e.g. 1000"
                        value={form.offset}
                        onChange={(e) => setForm({ ...form, offset: Number(e.target.value) })}
                      />
                    </div>
                  )}

                  <div className="col-12 col-md-4 mb-3">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.order}
                      onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                    />
                  </div>

                  <div className="col-12 col-md-4 mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  {/* Icon Selection */}
                  <div className="col-12 col-md-6 mb-3">
                    <label className="form-label">Choose Preset Icon</label>
                    <select
                      className="form-select"
                      value={form.icon}
                      onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    >
                      {DEFAULT_PRESET_ICONS.map((ico, i) => (
                        <option key={i} value={ico.value}>
                          {ico.label} ({ico.value})
                        </option>
                      ))}
                      {form.icon && !DEFAULT_PRESET_ICONS.some((p) => p.value === form.icon) && (
                        <option value={form.icon}>Custom Icon ({form.icon})</option>
                      )}
                    </select>
                  </div>

                  <div className="col-12 col-md-6 mb-3">
                    <label className="form-label">Upload New Custom Icon Image</label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="form-control"
                      accept="image/*"
                      onChange={(e) => setForm({ ...form, iconFile: e.target.files[0] })}
                    />
                    <small className="text-muted">Recommended: PNG / SVG with transparent background (~64x64px)</small>
                  </div>

                  <div className="col-12 text-end mt-2">
                    {editId && (
                      <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={resetForm}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    )}
                    <button type="submit" className="btn sitebtn px-4" disabled={loading}>
                      <i className="far fa-check-circle me-1"></i>
                      {loading ? "Saving..." : editId ? "Update Stat Card" : "Add Stat Card"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* 4. Complete Stat Cards Table */}
          <div className="x_panel" style={{ borderRadius: "12px", border: "1px solid #e3e6f0" }}>
            <div className="x_title">
              <h2>
                <i className="far fa-list-alt me-2" style={{ color: "#bf3272" }}></i> All Configured Stat Cards
              </h2>
              <div className="clearfix" />
            </div>

            <div className="x_content">
              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "60px" }}>Order</th>
                      <th style={{ width: "70px" }}>Icon</th>
                      <th>Label / Title</th>
                      <th>Key</th>
                      <th>Mode</th>
                      <th>Current Display</th>
                      <th>Live DB</th>
                      <th>Status</th>
                      <th style={{ width: "130px", textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.map((s, index) => (
                      <tr key={s._id || index}>
                        <td>
                          <strong>#{s.order ?? index + 1}</strong>
                        </td>
                        <td>
                          <img
                            src={resolveIconUrl(s.icon)}
                            alt={s.label}
                            style={{
                              width: "32px",
                              height: "32px",
                              objectFit: "contain",
                              backgroundColor: "#bf3272",
                              padding: "4px",
                              borderRadius: "6px",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        </td>
                        <td>
                          <strong>{s.label}</strong>
                        </td>
                        <td>
                          <code>{s.key}</code>
                        </td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              backgroundColor:
                                s.countType === "Manual"
                                  ? "#ffc107"
                                  : s.countType === "AutoWithOffset"
                                  ? "#17a2b8"
                                  : "#28a745",
                              color: s.countType === "Manual" ? "#000" : "#fff",
                            }}
                          >
                            {s.countType === "Manual"
                              ? "Manual (Custom)"
                              : s.countType === "AutoWithOffset"
                              ? `Auto + ${s.offset}`
                              : "Auto (Live DB)"}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: "1.05rem", fontWeight: "700", color: "#bf3272" }}>
                            {Number(s.currentCount || 0).toLocaleString()}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {Number(liveMap[s.key] ?? s.liveCount ?? 0).toLocaleString()}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              s.status === "Active" ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {s.status}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-info me-1"
                            title="Edit Stat"
                            onClick={() => handleEdit(s)}
                          >
                            <i className="far fa-edit"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            title="Delete Stat"
                            onClick={() => handleDelete(s._id, s.label)}
                          >
                            <i className="far fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {stats.length === 0 && !loading && (
                      <tr>
                        <td colSpan="9" className="text-center text-muted py-4">
                          No stats configured.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
